import * as D from 'io-ts/lib/Decoder'

import http from './http'
import { ShowDecoder, Show } from 'data/Show'
import { decodeAt, withDefault } from 'decode/util'
import { Pass, PassDecoder, PassForNonPaidEvents, PassForNonPaidEventsDecoder } from 'data/Pass'
import { AccessCode, AccessCodeDecoder } from 'data/AccessCode'
import { PassTierRemainingPasses, PassTierRemainingPassesDecoder } from 'data/PassTier'
import { isFailure } from './data/ApiResult'

const baseUrlV1 = '/api/v1'
const baseUrlV2 = '/api/v2'

/*
 * POST
 */

export const postLUA = http.post<{ showId: string; emoji: string; timestamp: string; isUnicode: boolean }>(
    `${baseUrlV2}/live-action`,
)

/*
 * GET
 */

export const getShows = http.get<Show[]>(`${baseUrlV1}/shows`, decodeAt('shows', D.array(ShowDecoder)))

export const getShowsBySeriesId = (seriesId: string) =>
    http.get<Show[]>(`${baseUrlV1}/shows/series/${seriesId}`, decodeAt('shows', D.array(ShowDecoder)))()

export const getShow = (path: string) => http.get<Show>(`${baseUrlV1}/show/${path}`, decodeAt('show', ShowDecoder))()

export const getArtistShow = (id: string) =>
    http.get<Show>(`${baseUrlV1}/artist/show/${id}`, decodeAt('show', ShowDecoder))()

export const getAllPasses = http.get<{
    passes: Pass[]
    remainingPasses: number | null
    remainingForPassTiers: PassTierRemainingPasses[]
}>(
    `${baseUrlV1}/passes`,
    D.type({
        passes: D.array(PassDecoder),
        remainingPasses: D.nullable(D.number),
        remainingForPassTiers: D.array(PassTierRemainingPassesDecoder),
    }),
)

export const getViewerCount = (showId: string) =>
    http.get<number>(`${baseUrlV1}/shows/${showId}/viewers/count`, decodeAt('viewerCount', D.number))()

export const postFreePass = http.post<
    {
        tierFormValues: any
        email: string
        showId: string
        passTierId: string
    },
    Pass
>(`${baseUrlV1}/passes`, decodeAt('pass', PassDecoder))

export const postPass = http.post<
    {
        showId: string
        fullName: string
        email: string
    },
    PassForNonPaidEvents
>(`${baseUrlV1}/passes`, decodeAt('pass', PassForNonPaidEventsDecoder))

export const fetchLocation = async (link: string) => {
    const LocationResultDecoder = D.type({
        name: D.string,
        _links: D.type({
            'city:admin1_division': D.type({
                name: D.string,
            }),
            'city:country': D.type({
                name: D.string,
            }),
        }),
    })

    interface LocationResult extends D.TypeOf<typeof LocationResultDecoder> {}

    const result = await http.get<LocationResult>(
        link,
        LocationResultDecoder,
    )({
        withCredentials: false,
    })

    if (isFailure(result)) {
        console.error(`Something happened while decoding the location ${result.error.toString()}`)
        return null
    }

    const { name, _links } = result.data

    return {
        country: _links['city:country'].name,
        region: _links['city:admin1_division'].name,
        city: name,
    }
}

export const fetchCities = async (search: string, limit: number = 10) => {
    const SearchResultDecoder = D.type({
        'city:search-results': withDefault(
            [],
            D.array(
                D.type({
                    matching_full_name: D.string,
                    _links: D.type({
                        'city:item': D.type({
                            href: D.string,
                        }),
                    }),
                }),
            ),
        ),
    })

    interface SearchResults extends D.TypeOf<typeof SearchResultDecoder> {}

    const result = await http.get<SearchResults>(
        `${process.env.REACT_APP_TELEPORT_API_BASE_URL}/cities`,
        decodeAt('_embedded', SearchResultDecoder),
    )({
        withCredentials: false,
        params: {
            search,
            limit,
        },
    })

    if (isFailure(result)) {
        console.error(`Something happened while decoding the cities ${result.error.toString()}`)
        return []
    }

    return result.data['city:search-results'].map(result => ({
        name: result.matching_full_name,
        link: result._links['city:item'].href,
    }))
}

export const validateAccessCode = ({ showId, email, displayName, accessCode }) =>
    http.patch<
        {
            showId: string
            email: string
            displayName: string
        },
        AccessCode
    >(
        `${baseUrlV1}/access-code/code/${accessCode}`,
        AccessCodeDecoder,
    )({ showId, email, displayName })

export const subscribeToWaitingList = ({ email }) =>
    http.post<{
        email: string
    }>(`${baseUrlV1}/waiting-list`)({ email })
